import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { animated, useTransition } from "@react-spring/web";
import { toast } from "react-toastify";
import _ from "lodash";

import Button from "src/components/button";
import Toast from "src/components/toast";
import { WorkspaceSelector } from "src/pages/experiences/storyboards/components/workspaceSelector";
import { updateCleverstoryWorkspace } from "src/utils/auth.interceptor";
import NewFieldDropdown from "./components/newFieldDropdown";

import MenuPane from "./components/menuPane";
import DetailView from "./components/detailView";

import { useQueryClient } from "@tanstack/react-query";
import {
  useCustomFormFieldDetailQuery,
  useCustomFormFieldsQuery,
  useCreateCustomFormFieldMutation,
  useUpdateCustomFormFieldMutation,
  useDeleteCustomFormFieldMutation,
} from "src/query/settings/customFormFields";
import { CUSTOM_FORM_FIELDS } from "src/query/queryKeys";

import { CustomFormField, FormFieldType } from "src/types/settings/customFormFields";
import { WorkspaceItem } from "src/types/experiences/workspaces";

import styles from "./styles.module.scss";

const CustomFormFields: React.FC = () => {
  const queryClient = useQueryClient();
  const workspaces: WorkspaceItem[] = useSelector(
    (state: any) => state.experiences.workspaces.items ?? [],
  );

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>("");
  const [selectedFieldId, setSelectedFieldId] = useState<string>("");
  const [localField, setLocalField] = useState<CustomFormField | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showDeleteSlider, setShowDeleteSlider] = useState(false);

  // Set default workspace once workspaces load and wire the CS-WORKSPACE-ID header
  useEffect(() => {
    if (workspaces.length > 0 && !selectedWorkspaceId) {
      const firstId = workspaces[0].id;
      setSelectedWorkspaceId(firstId);
      updateCleverstoryWorkspace(firstId);
    }
  }, [workspaces, selectedWorkspaceId]);

  const { fields, hasNextPage, isFetchingNextPage, loadMore, refetch: refetchList, isLoading: isListLoading } =
    useCustomFormFieldsQuery(selectedWorkspaceId);

  const { data: fieldDetail, refetch: refetchDetail } = useCustomFormFieldDetailQuery(selectedFieldId);

  const createMutation = useCreateCustomFormFieldMutation();
  const updateMutation = useUpdateCustomFormFieldMutation();
  const deleteMutation = useDeleteCustomFormFieldMutation();

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // When the fetched detail arrives, sync it to local state (unless we are in create mode)
  useEffect(() => {
    if (fieldDetail && !isCreating) {
      setLocalField(_.cloneDeep(fieldDetail));
    }
  }, [fieldDetail, isCreating]);

  // Auto-select first field when list loads
  useEffect(() => {
    if (fields.length > 0 && !selectedFieldId && !isCreating) {
      setSelectedFieldId(fields[0].id);
    }
  }, [fields, selectedFieldId, isCreating]);

  // Reset selection when workspace changes
  useEffect(() => {
    setSelectedFieldId("");
    setLocalField(null);
    setIsCreating(false);
  }, [selectedWorkspaceId]);

  const isModified = (() => {
    if (isCreating) return !!localField;
    if (!localField || !fieldDetail) return false;
    return !_.isEqual(localField, fieldDetail);
  })();

  const actionTransitions = useTransition(isModified, {
    from: { opacity: 0, transform: "translateY(-10px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(-10px)" },
    config: { tension: 220, friction: 20 },
  });

  const handleSelectField = (field: CustomFormField) => {
    if (isModified) return; // guard: discard unsaved changes first
    setIsCreating(false);
    setSelectedFieldId(field.id);
    setLocalField(null);
  };

  const handleNewField = (type: FormFieldType) => {
    setIsCreating(true);
    setSelectedFieldId("");
    setLocalField({
      id: "",
      name: "Untitled",
      type,
      placeholder: "",
      instructions: "",
      helpText: "",
      initialState: true,
      options: type === "list_field" ? [{ id: `opt-${Date.now()}`, value: "Option 1" }] : [],
      modifiedDate: Date.now(),
    });
  };

  const buildCreatePayload = (field: CustomFormField) => {
    const base = {
      name: field.name,
      type: field.type,
      entityType: "FORM" as const,
      helpText: field.helpText ?? "",
      placeholder: field.placeholder ?? "",
      multiselect: false,
    };

    if (field.type === "boolean_field") {
      return { ...base, instructions: field.instructions ?? "", initialState: field.initialState ?? true };
    }

    if (field.type === "list_field") {
      return { ...base, options: field.options ?? [] };
    }

    return base;
  };

  const handleSave = () => {
    if (!localField) return;

    if (!localField.name.trim()) {
      toast(<Toast.warning heading="Warning" message="Field name cannot be empty." />);
      return;
    }

    if (isCreating) {
      createMutation.mutate(
        buildCreatePayload(localField),
        {
          onSuccess: (created) => {
            toast(<Toast.Sucess heading="Success" message="Form field created." />);
            // Append directly to cache — no network refetch, no loading flicker
            queryClient.setQueryData(
              [CUSTOM_FORM_FIELDS, selectedWorkspaceId],
              (old: any) => {
                if (!old?.pages?.length) return old;
                const pages = [...old.pages];
                pages[0] = {
                  ...pages[0],
                  records: [created, ...(pages[0].records ?? [])],
                };
                return { ...old, pages };
              },
            );
            setIsCreating(false);
            setSelectedFieldId(created.id);
            setLocalField(_.cloneDeep(created)); // populate immediately from response
          },
          onError: () => {
            toast(<Toast.Failure heading="Error" message="Failed to create form field." />);
          },
        },
      );
    } else {
      updateMutation.mutate(
        {
          id: localField.id,
          data: {
            id: localField.id,
            name: localField.name,
            type: localField.type,
            placeholder: localField.placeholder,
            instructions: localField.instructions,
            helpText: localField.helpText,
            initialState: localField.initialState,
            options: localField.options,
            entityType: localField.entityType ?? "FORM",
            userDefined: localField.userDefined ?? true,
            uid: localField.uid ?? null,
            createdDate: localField.createdDate,
            modifiedDate: Date.now(),
          },
        },
        {
          onSuccess: () => {
            toast(<Toast.Sucess heading="Success" message="Saved changes." />);
            refetchDetail();
          },
          onError: () => {
            toast(<Toast.Failure heading="Error" message="Failed to save changes." />);
          },
        },
      );
    }
  };

  const handleCancel = () => {
    if (isCreating) {
      setIsCreating(false);
      setLocalField(fieldDetail ? _.cloneDeep(fieldDetail) : null);
      if (!selectedFieldId && fields.length > 0) {
        setSelectedFieldId(fields[0].id);
      }
    } else {
      setLocalField(fieldDetail ? _.cloneDeep(fieldDetail) : null);
    }
  };

  const handleDeleteClick = () => setShowDeleteSlider(true);

  const handleConfirmDelete = () => {
    if (!localField?.id) return;
    deleteMutation.mutate(localField.id, {
      onSuccess: () => {
        toast(<Toast.Sucess heading="Success" message="Form field deleted." />);
        setShowDeleteSlider(false);
        setSelectedFieldId("");
        setLocalField(null);
        refetchList();
      },
      onError: () => {
        toast(<Toast.Failure heading="Error" message="Failed to delete form field." />);
      },
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.actionBar}>
        <div className={styles.header}>
          <div className={styles.title}>Custom Form Fields</div>
          <div className={styles.subTitle}>One space to manage them all</div>
        </div>

        <div className={styles.controls}>
          {actionTransitions((style, modified) =>
            modified ? (
              <animated.div style={style} className={styles.saveActions}>
                <div className={styles.actionsInfo}>click here to</div>
                <Button
                  variant="primary"
                  icon="paperflite-tick"
                  text={isSaving ? "Saving..." : "Save"}
                  disabled={isSaving}
                  onClick={handleSave}
                />
                <Button variant="secondary" text="Cancel" onClick={handleCancel} disabled={isSaving} />
              </animated.div>
            ) : (
              <animated.div style={style} className={styles.defaultActions}>
                <NewFieldDropdown onSelect={handleNewField} />
                {workspaces.length > 0 && (
                  <WorkspaceSelector
                    className={styles.workspaceSelector}
                    workspaces={workspaces}
                    selectedId={selectedWorkspaceId}
                    onSelect={(id) => {
                      updateCleverstoryWorkspace(id);
                      setSelectedWorkspaceId(id);
                    }}
                  />
                )}
              </animated.div>
            )
          )}
        </div>
      </div>

      <div className={styles.body}>
        {isListLoading ? (
          <div className={styles.loading}><p>Loading...</p></div>
        ) : (
          <>
            <div className={styles.leftPanel}>
              <MenuPane
                fields={isCreating && localField ? [localField!, ...fields] : fields}
                selectedFieldId={isCreating ? "" : selectedFieldId}
                onSelectField={handleSelectField}
                hasMore={!!hasNextPage}
                isLoading={isFetchingNextPage}
                loadMore={loadMore}
              />
            </div>

            <div className={styles.rightPanel}>
              {!!selectedFieldId && !localField && !isCreating ? (
                <DetailSkeleton />
              ) : localField ? (
                <DetailView
                  field={localField!}
                  onChange={setLocalField}
                  onDelete={handleDeleteClick}
                  showDeleteSlider={showDeleteSlider}
                  onCloseDelete={() => setShowDeleteSlider(false)}
                  onConfirmDelete={handleConfirmDelete}
                  isDeleting={deleteMutation.isPending}
                />
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomFormFields;


const DetailSkeleton: React.FC = () => (
  <div className={styles.detailSkeleton}>
    <div className={styles.loadingDetailTitle} />
    <div className={styles.loadingDetailField} />
    <div className={styles.loadingDetailField} />
    <div className={styles.loadingDetailFieldShort} />
  </div>
);
